import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png'; // Adjust the path as necessary

import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    isFeafured: 'no', // Default to 'no'
    showDate: 'no', // Default to 'no'
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData('proccess.php', 'event_V2'); // Fetch events data
        if (response.success) {
          setEvents(response.data);
        } else {
          toast.error(response.message || "Failed to load events data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch events");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      title: '',
      description: '',
      image: '',
      isFeafured: 'no', // Default to 'no'
      showDate: 'no', // Default to 'no'
      startDate: '',
      endDate: ''
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (event) => {
    setSelectedEvent(event);
    
    // Convert the isFeafured value to string 'yes' or 'no'
    let featuredValue = 'no';
    if (event.isFeafured === 'yes' || event.isFeafured === 1 || event.isFeafured === true || event.isFeafured === '1') {
      featuredValue = 'yes';
    }

    // Convert the isFeafured value to string 'yes' or 'no'
    let showDateValue = 'no';
    if (event.showDate === 'yes' || event.showDate === 1 || event.showDate === true || event.showDate === '1') {
      showDateValue = 'yes';
    }
    
    setFormData({
      title: event.title || '',
      description: event.description || '',
      image: event.image || '',
      isFeafured: featuredValue,
      showDate: showDateValue,
      startDate: event.startDate ? event.startDate.split(' ')[0] : '',
      endDate: event.endDate ? event.endDate.split(' ')[0] : ''
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (event) => {
    setEventToDelete(event);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php', 'event_V2', eventToDelete.id); // Delete event by ID
      if (response.success) {
        toast.success(`Event deleted successfully`);
        setEvents(events.filter(event => event.id !== eventToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete event");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete event");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setEventToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      image: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare form data for submission
    const submitData = new FormData();
    submitData.append('title', formData.title);
    submitData.append('description', formData.description);
    submitData.append('isFeafured', formData.isFeafured); // Send as 'yes' or 'no'
    submitData.append('showDate', formData.showDate); // Send as 'yes' or 'no'
    submitData.append('startDate', formData.startDate);
    submitData.append('endDate', formData.endDate);
    
    // Handle image
    if (formData.image instanceof File) {
      submitData.append('image', formData.image);
    } else if (formData.image && typeof formData.image === 'string') {
      submitData.append('existing_image', formData.image);
    }

    try {
      let response;
      if (selectedEvent) {
        // Update existing event
        response = await updateData('proccess.php', 'event_V2', formData, selectedEvent.id);
        if (response.success) {
          toast.success(`Event updated successfully`);
          setEvents(response.data);
        }
      } else {
        // Add new event
        response = await addData('proccess.php', 'event_V2', formData);
        if (response.success) {
          toast.success(`Event added successfully`);
          setEvents(response.data);
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedEvent(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    { field: "title", headerName: "Event Title", flex: 1, minWidth: 120 },
    { field: "description", headerName: "Description", flex: 1, minWidth: 120 },
    {
      field: "image",
      headerName: "Event Image",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value ? (
            <img
              src={`https://mindsinaction.com.na/assets/media/pictures/${params.value}`}
              alt={params.value || "Event"}
              style={{ width: 50, height: 50, objectFit: "cover", cursor: "pointer" }}
              onClick={() => window.open(`https://mindsinaction.com.na/assets/media/pictures/${params.value}`, "_blank")}
            />
          ) : (
            "No image"
          )}
        </Box>
      )
    },
    { 
      field: "startDate", 
      headerName: "From", 
      flex: 0.8, 
      minWidth: 100,
      renderCell: (params) => (
        <Typography>
          {params.value ? new Date(params.value).toLocaleDateString() : ''}
        </Typography>
      )
    },
    { 
      field: "endDate", 
      headerName: "Until", 
      flex: 0.8, 
      minWidth: 100,
      renderCell: (params) => (
        <Typography>
          {params.value ? new Date(params.value).toLocaleDateString() : ''}
        </Typography>
      )
    },
    { 
      field: "dateCreated", 
      headerName: "Date Created", 
      flex: 0.8, 
      minWidth: 120,
      renderCell: (params) => (
        <Typography>
          {params.value ? new Date(params.value).toLocaleString() : ''}
        </Typography>
      )
    },
    {
      field: "isFeafured",
      headerName: "Featured",
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Box>
          {params.value === 'yes' ? (
            <Typography 
              sx={{ 
                color: colors.green[400], 
                fontWeight: "bold",
                backgroundColor: colors.green[100],
                padding: "4px 8px",
                borderRadius: "4px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px"
              }}
            >
              <CheckIcon fontSize="small" /> Yes
            </Typography>
          ) : (
            <Typography 
              sx={{ 
                color: colors.orange[400],
                backgroundColor: colors.orange[100],
                padding: "4px 8px",
                borderRadius: "4px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px"
              }}
            >
              <ClearIcon fontSize="small" /> No
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "showDate",
      headerName: "Show Event Date",
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Box>
          {params.value === 'yes' ? (
            <Typography 
              sx={{ 
                color: colors.green[400], 
                fontWeight: "bold",
                backgroundColor: colors.green[100],
                padding: "4px 8px",
                borderRadius: "4px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px"
              }}
            >
              <CheckIcon fontSize="small" /> Yes
            </Typography>
          ) : (
            <Typography 
              sx={{ 
                color: colors.orange[400],
                backgroundColor: colors.orange[100],
                padding: "4px 8px",
                borderRadius: "4px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px"
              }}
            >
              <ClearIcon fontSize="small" /> No
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No events available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedEvent(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedEvent ? "Edit Event" : "Add New Event"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{ width: "100%", padding: "1rem" }}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField
                style={{ width: "100%" }}
                id="title"
                name="title"
                label="Event Title"
                variant="standard"
                value={formData.title}
                onChange={handleFormChange}
                required
              />
              
              <TextField
                style={{ width: "100%" }}
                id="description"
                name="description"
                label="Description"
                multiline
                minRows={4}
                maxRows={12}
                variant="standard"
                value={formData.description}
                onChange={handleFormChange}
                required
              />
              
              <Box display="flex" gap="1rem" width="100%">
                <FormControl component="fieldset" style={{ width: "50%" }}>
                  <FormLabel component="legend">Featured Event</FormLabel>
                  <RadioGroup
                    row
                    id="isFeafured"
                    name="isFeafured"
                    value={formData.isFeafured}
                    onChange={handleFormChange}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Box display="flex" gap="1rem" width="100%">
                <FormControl component="fieldset" style={{ width: "50%" }}>
                  <FormLabel component="legend">Show Event Date</FormLabel>
                  <RadioGroup
                    row
                    id="showDate"
                    name="showDate"
                    value={formData.showDate}
                    onChange={handleFormChange}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Box>
              
              <Box display="flex" gap="1rem" width="100%">
                <TextField
                  style={{ width: "50%" }}
                  id="startDate"
                  name="startDate"
                  label="Start Date"
                  type="date"
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  value={formData.startDate}
                  onChange={handleFormChange}
                  required
                />
                
                <TextField
                  style={{ width: "50%" }}
                  id="endDate"
                  name="endDate"
                  label="End Date"
                  type="date"
                  variant="standard"
                  InputLabelProps={{ shrink: true }}
                  value={formData.endDate}
                  onChange={handleFormChange}
                  required
                />
              </Box>
              
              <Box width={"100%"}>
                <label>Event Image</label>
                <Input
                  type="file"
                  style={{ width: "100%", marginTop: "0.5rem" }}
                  id="image"
                  name="image"
                  onChange={handleFileChange}
                  inputProps={{ accept: "image/*" }}
                  required={!selectedEvent}
                />
                {formData.image && typeof formData.image === 'string' && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Current image: {formData.image}
                  </Typography>
                )}
              </Box>
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
              setSelectedEvent(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedEvent ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{eventToDelete?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{ mb: "5px" }}
          >
            Events Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            Manage events
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{
            backgroundColor: colors.blue[400],
            '&:hover': {
              backgroundColor: colors.blue[500],
            }
          }}
        >
          Add Event
        </Button>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "calc(100vh - 250px)", width: "100%" }} backgroundColor={colors.dark[300]} padding={".2rem"} mt={"1rem"}>
        <DataGrid
          rows={events}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: "none",
              backgroundColor: "none"
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Events;